import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiShoppingCart, FiStar } from 'react-icons/fi';
import { toast } from 'sonner';

const ProductDetail = ({ updateCartCount }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [canReview, setCanReview] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    customer_name: '',
    customer_email: '',
    rating: 5,
    review_text: '',
    image_id: null
  });
  const [uploadedImage, setUploadedImage] = useState(null);

  useEffect(() => {
    loadProductAndReviews();
  }, [id]);

  const loadProductAndReviews = async () => {
    try {
      const [productRes, reviewsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products/${id}`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews/product/${id}`)
      ]);
      
      const productData = await productRes.json();
      const reviewsData = await reviewsRes.json();
      
      setProduct(productData);
      setReviews(reviewsData);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    const sessionId = localStorage.getItem('session_id') || generateSessionId();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cart/${sessionId}/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: product.id,
          quantity: quantity,
          variant: selectedVariant
        })
      });

      if (response.ok) {
        toast.success('Added to cart!');
        updateCartCount();
      } else {
        toast.error('Failed to add to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add to cart');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews/upload-image`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setReviewForm({ ...reviewForm, image_id: data.image_id });
        setUploadedImage(URL.createObjectURL(file));
        toast.success('Image uploaded!');
      } else {
        toast.error('Image upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Image upload failed');
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: id,
          ...reviewForm
        })
      });

      if (response.ok) {
        toast.success('Review submitted! It will appear after admin approval.');
        setShowReviewForm(false);
        setReviewForm({
          customer_name: '',
          customer_email: '',
          rating: 5,
          review_text: '',
          image_id: null
        });
        setUploadedImage(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit review');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit review');
    }
  };

  const generateSessionId = () => {
    const id = 'session_' + Math.random().toString(36).substr(2, 9) + Date.now();
    localStorage.setItem('session_id', id);
    return id;
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <FiStar
        key={i}
        size={16}
        fill={i < rating ? '#FFB800' : 'none'}
        stroke={i < rating ? '#FFB800' : '#FFEAB3'}
      />
    ));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="product-detail-loading">
        <div className="text-2xl font-['Fredoka'] text-[#FFB800]">Loading...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="product-not-found">
        <div className="text-center">
          <h2 className="text-3xl font-['Fredoka'] text-[#3A1C00] mb-4">Product not found</h2>
          <button onClick={() => navigate('/products')} className="btn-primary">
            Back to Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="content-wrapper py-12 px-4 sm:px-6 lg:px-8 min-h-screen" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <button 
            onClick={() => navigate('/products')} 
            className="text-[#FFB800] font-['Fredoka'] font-bold mb-8 hover:text-[#FF9E00] transition-colors"
            data-testid="back-to-products-button"
          >
            ← Back to Products
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="rounded-[3rem] overflow-hidden bg-white shadow-[0_20px_40px_rgba(255,184,0,0.15)]" data-testid="product-image-container">
              <img 
                src={product.image} 
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-detail-image"
              />
            </div>

            <div className="space-y-6" data-testid="product-info-container">
              <div>
                <div className="flex items-center gap-4 mb-4">
                  <span className="badge badge-stock" data-testid="product-detail-stock">{product.stock_status}</span>
                  {product.review_count > 0 && (
                    <div className="flex items-center gap-2">
                      <div className="flex">{renderStars(Math.round(product.average_rating))}</div>
                      <span className="text-[#7D5A35] text-sm">({product.review_count} reviews)</span>
                    </div>
                  )}
                </div>
                <h1 className="text-5xl font-black font-['Fredoka'] text-[#3A1C00] mb-4" data-testid="product-detail-name">
                  {product.name}
                </h1>
                <p className="text-4xl font-black font-['Fredoka'] text-[#FFB800] mb-6" data-testid="product-detail-price">
                  ${product.price.toFixed(2)}
                </p>
                <p className="text-lg text-[#7D5A35] leading-relaxed" data-testid="product-detail-description">
                  {product.description}
                </p>
              </div>

              {product.variants && product.variants.length > 0 && (
                <div data-testid="product-variants-section">
                  <h3 className="text-xl font-bold font-['Fredoka'] text-[#3A1C00] mb-3">Choose Variant:</h3>
                  <div className="flex flex-wrap gap-3">
                    {product.variants.map((variant, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedVariant(variant.name)}
                        className={`px-6 py-3 rounded-full font-bold transition-all ${
                          selectedVariant === variant.name
                            ? 'bg-[#FF6B9E] text-white border-2 border-[#FF6B9E]'
                            : 'bg-[#FFF1E5] text-[#FF6B9E] border-2 border-[#FF6B9E]/20 hover:border-[#FF6B9E]'
                        }`}
                        data-testid={`variant-button-${index}`}
                      >
                        {variant.name} - ${variant.price.toFixed(2)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div data-testid="quantity-section">
                <h3 className="text-xl font-bold font-['Fredoka'] text-[#3A1C00] mb-3">Quantity:</h3>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-12 h-12 rounded-full bg-[#FFF1E5] text-[#3A1C00] font-bold text-xl hover:bg-[#FFB800] hover:text-white transition-all"
                    data-testid="decrease-quantity-button"
                  >
                    -
                  </button>
                  <span className="text-2xl font-bold font-['Fredoka'] text-[#3A1C00] w-12 text-center" data-testid="quantity-display">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-12 h-12 rounded-full bg-[#FFF1E5] text-[#3A1C00] font-bold text-xl hover:bg-[#FFB800] hover:text-white transition-all"
                    data-testid="increase-quantity-button"
                  >
                    +
                  </button>
                </div>
              </div>

              <button
                onClick={handleAddToCart}
                className="btn-primary w-full flex items-center justify-center gap-3 text-xl py-6"
                data-testid="add-to-cart-button"
              >
                <FiShoppingCart size={24} />
                Add to Cart
              </button>

              <div className="space-y-6 pt-8" data-testid="product-details-tabs">
                <div className="bg-white rounded-3xl p-8 shadow-sm">
                  <h3 className="text-2xl font-bold font-['Fredoka'] text-[#3A1C00] mb-4">Ingredients</h3>
                  <p className="text-[#7D5A35] leading-relaxed" data-testid="product-ingredients">{product.ingredients}</p>
                </div>

                <div className="bg-white rounded-3xl p-8 shadow-sm">
                  <h3 className="text-2xl font-bold font-['Fredoka'] text-[#3A1C00] mb-4">Benefits</h3>
                  <p className="text-[#7D5A35] leading-relaxed" data-testid="product-benefits">{product.benefits}</p>
                </div>

                <div className="bg-white rounded-3xl p-8 shadow-sm">
                  <h3 className="text-2xl font-bold font-['Fredoka'] text-[#3A1C00] mb-4">How to Use</h3>
                  <p className="text-[#7D5A35] leading-relaxed" data-testid="product-how-to-use">{product.how_to_use}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Reviews Section */}
          <div className="mt-16" data-testid="reviews-section">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-4xl font-black font-['Fredoka'] text-[#3A1C00]">Customer Reviews</h2>
              <button
                onClick={() => setShowReviewForm(!showReviewForm)}
                className="btn-secondary"
                data-testid="write-review-button"
              >
                Write a Review
              </button>
            </div>

            {showReviewForm && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="bg-white rounded-3xl p-8 shadow-sm mb-8"
                data-testid="review-form"
              >
                <h3 className="text-2xl font-bold font-['Fredoka'] text-[#3A1C00] mb-6">Write Your Review</h3>
                <form onSubmit={handleSubmitReview} className="space-y-4">
                  <div>
                    <label className="block text-[#3A1C00] font-bold mb-2">Name</label>
                    <input
                      type="text"
                      value={reviewForm.customer_name}
                      onChange={(e) => setReviewForm({ ...reviewForm, customer_name: e.target.value })}
                      className="admin-input"
                      required
                      data-testid="review-name-input"
                    />
                  </div>

                  <div>
                    <label className="block text-[#3A1C00] font-bold mb-2">Email (used to verify purchase)</label>
                    <input
                      type="email"
                      value={reviewForm.customer_email}
                      onChange={(e) => setReviewForm({ ...reviewForm, customer_email: e.target.value })}
                      className="admin-input"
                      required
                      data-testid="review-email-input"
                    />
                  </div>

                  <div>
                    <label className="block text-[#3A1C00] font-bold mb-2">Rating</label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                          className="text-3xl"
                          data-testid={`star-${star}`}
                        >
                          <FiStar
                            fill={star <= reviewForm.rating ? '#FFB800' : 'none'}
                            stroke={star <= reviewForm.rating ? '#FFB800' : '#FFEAB3'}
                          />
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#3A1C00] font-bold mb-2">Review</label>
                    <textarea
                      value={reviewForm.review_text}
                      onChange={(e) => setReviewForm({ ...reviewForm, review_text: e.target.value })}
                      className="admin-input"
                      rows="4"
                      required
                      data-testid="review-text-input"
                    ></textarea>
                  </div>

                  <div>
                    <label className="block text-[#3A1C00] font-bold mb-2">Add Photo (Optional)</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="admin-input"
                      data-testid="review-image-input"
                    />
                    {uploadedImage && (
                      <img src={uploadedImage} alt="Preview" className="mt-4 w-32 h-32 object-cover rounded-xl" />
                    )}
                  </div>

                  <div className="flex gap-4">
                    <button type="submit" className="btn-primary" data-testid="submit-review-button">
                      Submit Review
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowReviewForm(false)}
                      className="btn-secondary"
                      data-testid="cancel-review-button"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </motion.div>
            )}

            <div className="space-y-6" data-testid="reviews-list">
              {reviews.length === 0 ? (
                <p className="text-[#7D5A35] text-center py-12">No reviews yet. Be the first to review this product!</p>
              ) : (
                reviews.map((review, index) => (
                  <div key={review.id} className="bg-white rounded-3xl p-8 shadow-sm" data-testid={`review-${index}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h4 className="text-xl font-bold font-['Fredoka'] text-[#3A1C00]">{review.customer_name}</h4>
                        <div className="flex gap-1 mt-2">{renderStars(review.rating)}</div>
                      </div>
                      <span className="text-sm text-[#7D5A35]">
                        {new Date(review.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-[#7D5A35] leading-relaxed mb-4">{review.review_text}</p>
                    {review.image_url && (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${review.image_url}`}
                        alt="Review"
                        className="w-48 h-48 object-cover rounded-xl mb-4"
                      />
                    )}
                    
                    {/* Admin Replies */}
                    {review.replies && review.replies.length > 0 && (
                      <div className="mt-6 ml-8 space-y-4">
                        {review.replies.map((reply, replyIndex) => (
                          <div 
                            key={reply.id} 
                            className="bg-[#FFF1E5] rounded-2xl p-6 border-l-4 border-[#FFB800]"
                            data-testid={`reply-${index}-${replyIndex}`}
                          >
                            <div className="flex items-center gap-2 mb-3">
                              <span className="bg-[#FFB800] text-[#3A1C00] px-3 py-1 rounded-full text-sm font-bold">
                                Mango Cosmetics
                              </span>
                              <span className="text-xs text-[#7D5A35]">
                                {new Date(reply.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-[#3A1C00] leading-relaxed">{reply.reply_text}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProductDetail;